import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { GOOGLE_STATE_KEY } from "@/utils/googleOauth";
import { BrandLoader } from "@/components/BrandLoader";

/**
 * Retour du login Google OAuth (client NelDaari) — route /auth/google.
 * - state "web_…"    : échange du code côté backend puis connexion (web).
 * - state "native_…" : cette page tourne dans le navigateur intégré de l'app →
 *   relais du code vers l'app via le deep link neldaari://auth/google.
 */
export default function GoogleCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const processed = useRef(false);
  const [nativeUrl, setNativeUrl] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const code = params.get("code");
    const state = params.get("state") || "";
    if (params.get("error") || !code) {
      // access_denied (annulation) ou erreur Google : retour connexion sans crash
      navigate("/login", { replace: true });
      return;
    }
    if (state.startsWith("native_")) {
      const appUrl = `neldaari://auth/google?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
      setNativeUrl(appUrl);
      window.location.href = appUrl;
      return;
    }
    // Web : contrôle anti-CSRF du state émis au départ
    if (sessionStorage.getItem(GOOGLE_STATE_KEY) !== state) {
      navigate("/login", { replace: true });
      return;
    }
    sessionStorage.removeItem(GOOGLE_STATE_KEY);
    (async () => {
      try {
        const { data } = await apiClient.post("/auth/google/session", {
          code,
          redirect_uri: window.location.origin + "/auth/google",
        });
        setUser(data);
        navigate("/", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser, params]);

  if (nativeUrl) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6 text-center" data-testid="google-native-relay">
        <div>
          <div className="font-display text-lg font-bold mb-2">Retour vers l'app NelDaari…</div>
          <p className="text-sm text-mute mb-4">Returning to the NelDaari app…</p>
          <a href={nativeUrl} data-testid="google-native-relay-open" className="inline-flex rounded-full bg-brand-red text-white px-6 py-3 text-sm font-semibold">
            Ouvrir NelDaari / Open NelDaari
          </a>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-[60vh]">
      <BrandLoader message={t("common.loggingIn")} />
    </div>
  );
}
